import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { BRAND_PRIMARY } from "../../theme/theme";

/**
 * Animated Argus eye illustration — standalone, cursor-aware, with real
 * CSS 3D perspective.
 *
 * ─── Composition ───────────────────────────────────────────────────────
 * Five concentric layers, stacked back-to-front:
 *
 *   1. White backdrop disc (static)     — common.white, full container
 *   2. Outer blue ring stroke (static)  — radius OUTER_RING_R, 2px stroke
 *   3. Light-purple sclera disc         — radius SCLERA_R, fill #EDEAFF
 *   4. Blue iris                        — radius IRIS_R, fill BRAND_PRIMARY
 *   5. White catchlight                 — radius CATCHLIGHT_R, NW-offset
 *
 * All radii are declared in SVG-viewBox units (viewBox is 400×400, so r=200
 * would fill the full container). Tweak the `*_R` constants below to
 * rebalance the composition — `MASK_R_PCT` is derived from `SCLERA_R` so
 * the inner subject is automatically clipped to the sclera silhouette.
 *
 * ─── Motion ────────────────────────────────────────────────────────────
 * Layers 1 + 2 stay flat (rendered outside the perspective wrapper).
 * Layers 3 + 4 + 5 live inside a `perspective(PERSPECTIVE_PX)` camera and
 * share a `transformStyle: preserve-3d` ROTATED SUBJECT that tilts on X/Y
 * (`rotateX` / `rotateY`, up to MAX_TILT_DEG) toward the cursor — the
 * whole plane reads as a disc turning in space.
 *
 * On top of the shared tilt, each circle gets its OWN graduated parallax:
 *
 *                   translateZ   gaze travel
 *     Sclera        0            SCLERA_TRAVEL_PX     (smallest)
 *     Iris          IRIS_Z_PX    IRIS_TRAVEL_PX       (medium)
 *     Catchlight    CATCHLIGHT_  CATCHLIGHT_TRAVEL_PX (largest)
 *                   Z_PX
 *
 * Each layer translates toward the cursor by more than the one behind it,
 * which produces a classic foreground-faster-than-background parallax:
 * the catchlight glides across the iris, the iris slides over the sclera,
 * and the sclera barely nudges — selling real depth.
 *
 * On pointer-leave-window / tab-blur the subject eases back to rest. All
 * motion is `transform` + `opacity` only (per the theme rule in
 * `new-fe/src/theme/theme.ts`).
 */

// ─── Composition — radii (SVG-viewBox units, viewBox is 400×400) ─────────
const OUTER_RING_R = 180;
const SCLERA_R = 162; // small gap to the outer ring, per design
const IRIS_R = 62; // ~38% of sclera — matches the Pencil mockup proportion
const CATCHLIGHT_R = 14;
const CATCHLIGHT_CX = 182;
const CATCHLIGHT_CY = 182;

// Mask must clip to the sclera silhouette; 400-unit viewBox → SCLERA_R / 400.
const MASK_R_PCT = (SCLERA_R / 400) * 100;

// ─── Motion — tilt + graduated parallax tuning ───────────────────────────
// Classic foreground-first parallax: each circle translates toward the
// cursor by MORE than the layer behind it, so the composition reads as
// stacked depth planes. The sclera's tiny nudge hints at a curved surface,
// the iris slides visibly beneath the catchlight, and the catchlight is
// the fastest-moving highlight on top.
const MAX_TRACKING_RADIUS_PX = 520;
const MAX_TILT_DEG = 14; // how far the eye plane can rotate toward the cursor
// Travel + Z values are tuned at REFERENCE_SIZE_PX; at runtime they scale
// linearly with the container size so the same ArgusEye reads correctly at
// the onboarding hero (~300-400px) AND the /argus welcome card (64px). At
// 64px an absolute 12px iris travel is ~19% of the eye width — which made
// the iris visibly overflow the sclera. See F-overflow note in QA doc.
const REFERENCE_SIZE_PX = 300;
const SCLERA_TRAVEL_PX = 4;
const IRIS_TRAVEL_PX = 12;
const CATCHLIGHT_TRAVEL_PX = 22;
const SCLERA_Z_PX = 2;
const IRIS_Z_PX = 18;
const CATCHLIGHT_Z_PX = 32;
const PERSPECTIVE_PX = 900;

// Shared timing — "ease-out-expo" decelerating curve: motion is fast at
// the start and drags out slowly to a stop. Duration is long enough for
// the tail to actually read (260ms felt clipped).
const EASE_OUT = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_DURATION_MS = 420;

export const ArgusEye = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gaze, setGaze] = useState({ x: 0, y: 0 });
  const [sizeScale, setSizeScale] = useState(1);

  useEffect(() => {
    const resetGaze = () => setGaze({ x: 0, y: 0 });
    const handleMove = (e: MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist === 0) {
        resetGaze();
        return;
      }
      const clamped = Math.min(dist, MAX_TRACKING_RADIUS_PX);
      const factor = clamped / MAX_TRACKING_RADIUS_PX;
      setGaze({ x: (dx / dist) * factor, y: (dy / dist) * factor });
    };
    const handleOut = (e: MouseEvent) => {
      if (e.relatedTarget === null) resetGaze();
    };
    window.addEventListener("mousemove", handleMove, { passive: true });
    document.addEventListener("mouseout", handleOut);
    window.addEventListener("blur", resetGaze);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseout", handleOut);
      window.removeEventListener("blur", resetGaze);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const updateScale = () => {
      const rect = el.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      if (size > 0) setSizeScale(size / REFERENCE_SIZE_PX);
    };
    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // gaze.x > 0 → cursor is to the right → the eye rotates toward the right,
  // which means a POSITIVE rotateY around the vertical axis.
  // gaze.y > 0 → cursor is below → the eye tips down, so rotateX is NEGATIVE
  // (in CSS, positive rotateX tips the top toward the viewer).
  const tiltX = -gaze.y * MAX_TILT_DEG;
  const tiltY = gaze.x * MAX_TILT_DEG;
  const tiltTransform = `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
  const scleraTravel = SCLERA_TRAVEL_PX * sizeScale;
  const irisTravel = IRIS_TRAVEL_PX * sizeScale;
  const catchlightTravel = CATCHLIGHT_TRAVEL_PX * sizeScale;
  const scleraZ = SCLERA_Z_PX * sizeScale;
  const irisZ = IRIS_Z_PX * sizeScale;
  const catchlightZ = CATCHLIGHT_Z_PX * sizeScale;
  const scleraTransform = `translate3d(${gaze.x * scleraTravel}px, ${gaze.y * scleraTravel}px, ${scleraZ}px)`;
  const irisTransform = `translate3d(${gaze.x * irisTravel}px, ${gaze.y * irisTravel}px, ${irisZ}px)`;
  const catchlightTransform = `translate3d(${gaze.x * catchlightTravel}px, ${gaze.y * catchlightTravel}px, ${catchlightZ}px)`;

  return (
    <Box
      ref={containerRef}
      aria-hidden
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Outer frame (static) — white backdrop + thin blue ring stay
          flat. The 3D tilt is scoped to the inner eye only. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          bgcolor: "common.white",
          boxShadow: "0 2px 24px rgba(22, 0, 234, 0.04)",
        }}
      />
      <Box sx={{ position: "absolute", inset: 0 }}>
        <svg viewBox="0 0 400 400" width="100%" height="100%" role="presentation">
          <circle
            cx="200"
            cy="200"
            r={OUTER_RING_R}
            fill="none"
            stroke={BRAND_PRIMARY}
            strokeOpacity="0.9"
            strokeWidth="2"
          />
        </svg>
      </Box>

      {/* Perspective + sclera-silhouette mask wrapper. Everything inside
          is clipped to the sclera disc so raised layers can't poke past
          the outer blue ring. Mask radius derived from SCLERA_R. */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          perspective: `${PERSPECTIVE_PX}px`,
          WebkitMaskImage: `radial-gradient(circle, #000 ${MASK_R_PCT}%, rgba(0,0,0,0) ${MASK_R_PCT + 0.5}%)`,
          maskImage: `radial-gradient(circle, #000 ${MASK_R_PCT}%, rgba(0,0,0,0) ${MASK_R_PCT + 0.5}%)`,
        }}
      >
        {/* Rotated subject — sclera + iris + catchlight share the tilt
            plane. Each layer gets its own Z-raise + gaze translation on
            top of the shared rotation, so the parallax speed grows with
            each layer: sclera (slowest) → iris → catchlight (fastest). */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            transformStyle: "preserve-3d",
            transform: tiltTransform,
            transition: `transform ${EASE_DURATION_MS}ms ${EASE_OUT}`,
            willChange: "transform",
          }}
        >
          {/* Sclera disc — smallest parallax; barely nudges. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              transform: scleraTransform,
              transition: `transform ${EASE_DURATION_MS}ms ${EASE_OUT}`,
              willChange: "transform",
            }}
          >
            <svg viewBox="0 0 400 400" width="100%" height="100%" role="presentation">
              <circle cx="200" cy="200" r={SCLERA_R} fill="#EDEAFF" />
            </svg>
          </Box>

          {/* Iris — medium parallax; slides visibly over the sclera. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              transform: irisTransform,
              transition: `transform ${EASE_DURATION_MS}ms ${EASE_OUT}`,
              willChange: "transform",
            }}
          >
            <svg viewBox="0 0 400 400" width="100%" height="100%" role="presentation">
              <circle cx="200" cy="200" r={IRIS_R} fill={BRAND_PRIMARY} />
            </svg>
          </Box>

          {/* Catchlight — fastest parallax; glints across the iris. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              transform: catchlightTransform,
              transition: `transform ${EASE_DURATION_MS}ms ${EASE_OUT}`,
              willChange: "transform",
            }}
          >
            <svg viewBox="0 0 400 400" width="100%" height="100%" role="presentation">
              <circle cx={CATCHLIGHT_CX} cy={CATCHLIGHT_CY} r={CATCHLIGHT_R} fill="#FFFFFF" />
            </svg>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
